// src/components/pos/POSSettings.js
// Full POS configuration tab — imported by Settings.js
import { useState, useEffect } from 'react'
import { supabase } from '../../supabase'
import { POS_MODES, KARAT_OPTIONS, STAFF_ROLES } from './pos.constants'

const inp = {
  width: '100%', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
  padding: '9px 12px', fontSize: '12.5px', outline: 'none', background: 'var(--bg-card)',
  color: 'var(--text-primary)', fontFamily: 'var(--font-ui)', boxSizing: 'border-box',
  transition: 'border-color 0.15s',
}
const inpF = e => e.target.style.borderColor = 'var(--primary)'
const inpB = e => e.target.style.borderColor = 'var(--border)'

function Label({ children, optional }) {
  return (
    <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>
      {children}
      {optional && <span style={{ fontWeight: 400, opacity: 0.6, marginLeft: '4px' }}>optional</span>}
    </label>
  )
}

function SToggle({ label, desc, value, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)', gap: '12px' }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{label}</div>
        {desc && <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginTop: '2px', lineHeight: 1.5 }}>{desc}</div>}
      </div>
      <button onClick={() => onChange(!value)} type="button"
        style={{ width: '40px', height: '22px', borderRadius: 'var(--radius-full)', background: value ? 'var(--primary)' : 'var(--border)', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0, marginTop: '2px' }}>
        <div style={{ position: 'absolute', top: '2px', width: '18px', height: '18px', borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s', left: value ? '20px' : '2px' }} />
      </button>
    </div>
  )
}

function Card({ children, title, desc }) {
  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', overflow: 'hidden', boxShadow: 'var(--shadow-sm)', marginBottom: '14px' }}>
      {title && (
        <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border)', background: 'var(--bg-muted)', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '3px', height: '16px', borderRadius: '2px', background: 'var(--primary)', flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{title}</div>
            {desc && <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginTop: '1px' }}>{desc}</div>}
          </div>
        </div>
      )}
      <div style={{ padding: '16px 18px' }}>{children}</div>
    </div>
  )
}

function PinInput({ value, onChange, placeholder = 'Enter 4-digit PIN' }) {
  const [show, setShow] = useState(false)
  return (
    <div style={{ position: 'relative' }}>
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 4); onChange(v) }}
        placeholder={placeholder}
        maxLength={4}
        style={{ ...inp, paddingRight: '40px', fontFamily: 'monospace', letterSpacing: '0.2em', fontSize: '16px' }}
        onFocus={inpF} onBlur={inpB}
      />
      <button type="button" onClick={() => setShow(s => !s)}
        style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '11px', fontWeight: 600 }}>
        {show ? 'Hide' : 'Show'}
      </button>
    </div>
  )
}

export default function POSSettingsTab({ seller, showToast, plan }) {
  const sellerId = seller?.id
  const posMode  = seller?.pos_mode || 'standard'
  const isJewellery = posMode === 'jewellery'
  const canManageStaff = ['business'].includes(plan)

  // ── POS config state ──
  const [config, setConfig] = useState({
    pos_mode:           seller?.pos_mode          || 'standard',
    pos_return_window:  seller?.pos_return_window ?? 0,
    pos_require_pin:    seller?.pos_require_pin   ?? false,
    pos_default_branch: seller?.pos_default_branch || '',
    pos_receipt_footer: seller?.pos_receipt_footer || '',
    pos_tax_name:       seller?.pos_tax_name       || 'VAT',
    pos_tax_percent:    seller?.pos_tax_percent    ?? 0,
  })
  const [savingConfig, setSavingConfig] = useState(false)

  // ── Staff state ──
  const [staff, setStaff]           = useState([])
  const [staffLoading, setStaffLoading] = useState(false)
  const [showAddStaff, setShowAddStaff] = useState(false)
  const [editingStaff, setEditingStaff] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [staffForm, setStaffForm]   = useState({ name: '', role: 'cashier', branch: '', pin: '', pinConfirm: '' })
  const [staffSaving, setStaffSaving] = useState(false)
  const [staffError, setStaffError]   = useState('')

  // ── Gold rates state ──
  const [goldRates, setGoldRates]   = useState(null)
  const [savingGold, setSavingGold] = useState(false)
  const [goldForm, setGoldForm]     = useState({ rate_22k:'', rate_18k:'', rate_14k:'', rate_silver:'', rate_platinum:'', buyback_22k:'', buyback_18k:'', buyback_14k:'', buyback_silver:'' })

  useEffect(() => {
    if (sellerId) {
      fetchStaff()
      if (isJewellery) fetchGoldRates()
    }
  }, [sellerId, isJewellery]) // eslint-disable-line

  useEffect(() => {
    setConfig({
      pos_mode:           seller?.pos_mode          || 'standard',
      pos_return_window:  seller?.pos_return_window ?? 0,
      pos_require_pin:    seller?.pos_require_pin   ?? false,
      pos_default_branch: seller?.pos_default_branch || '',
      pos_receipt_footer: seller?.pos_receipt_footer || '',
      pos_tax_name:       seller?.pos_tax_name       || 'VAT',
      pos_tax_percent:    seller?.pos_tax_percent    ?? 0,
    })
  }, [seller?.id]) // eslint-disable-line

  const fetchStaff = async () => {
    setStaffLoading(true)
    const { data } = await supabase.from('seller_staff').select('id, name, role, branch, is_active, created_at').eq('seller_id', sellerId).order('name')
    setStaff(data || [])
    setStaffLoading(false)
  }

  const fetchGoldRates = async () => {
    const today = new Date().toISOString().split('T')[0]
    const { data } = await supabase.from('pos_gold_rates').select('*').eq('seller_id', sellerId).eq('effective_date', today).single()
    if (data) {
      setGoldRates(data)
      setGoldForm({ rate_22k: data.rate_22k||'', rate_18k: data.rate_18k||'', rate_14k: data.rate_14k||'', rate_silver: data.rate_silver||'', rate_platinum: data.rate_platinum||'', buyback_22k: data.buyback_22k||'', buyback_18k: data.buyback_18k||'', buyback_14k: data.buyback_14k||'', buyback_silver: data.buyback_silver||'' })
    }
  }

  // ── Save POS config ──
  const handleSaveConfig = async () => {
    setSavingConfig(true)
    await supabase.from('sellers').update({
      pos_mode:           config.pos_mode,
      pos_return_window:  Number(config.pos_return_window),
      pos_require_pin:    config.pos_require_pin,
      pos_default_branch: config.pos_default_branch || null,
      pos_receipt_footer: config.pos_receipt_footer || null,
      pos_tax_name:       config.pos_tax_name || 'VAT',
      pos_tax_percent:    Number(config.pos_tax_percent) || 0,
    }).eq('id', sellerId)
    setSavingConfig(false)
    showToast('POS settings saved')
  }

  // ── Staff operations ──
  const openAddStaff = () => {
    setStaffForm({ name:'', role:'cashier', branch:'', pin:'', pinConfirm:'' })
    setStaffError(''); setEditingStaff(null); setShowAddStaff(true)
  }

  const openEditStaff = (s) => {
    setStaffForm({ name: s.name, role: s.role, branch: s.branch||'', pin:'', pinConfirm:'' })
    setStaffError(''); setEditingStaff(s); setShowAddStaff(true)
  }

  const handleSaveStaff = async () => {
    if (!staffForm.name.trim()) { setStaffError('Name is required'); return }
    if (!editingStaff && staffForm.pin.length !== 4) { setStaffError('PIN must be exactly 4 digits'); return }
    if (staffForm.pin && staffForm.pin !== staffForm.pinConfirm) { setStaffError('PINs do not match'); return }

    setStaffSaving(true); setStaffError('')
    try {
      const { data, error } = await supabase.functions.invoke('hash-staff-pin', {
        body: {
          seller_id: sellerId,
          staff_id: editingStaff?.id || null,
          name: staffForm.name.trim(),
          pin: staffForm.pin || null,
          role: staffForm.role,
          branch: staffForm.branch || null,
        }
      })
      if (error || !data?.success) throw new Error(error?.message || 'Failed to save staff')
      await fetchStaff()
      setShowAddStaff(false)
      showToast(editingStaff ? 'Staff updated' : 'Staff added')
    } catch (err) {
      setStaffError(err.message || 'Something went wrong')
    }
    setStaffSaving(false)
  }

  const handleDeleteStaff = async (id) => {
    await supabase.from('seller_staff').update({ is_active: false }).eq('id', id)
    await fetchStaff()
    setDeleteConfirm(null)
    showToast('Staff removed')
  }

  const handleToggleStaffActive = async (id, current) => {
    await supabase.from('seller_staff').update({ is_active: !current }).eq('id', id)
    await fetchStaff()
  }

  // ── Save gold rates ──
  const handleSaveGoldRates = async () => {
    setSavingGold(true)
    const today = new Date().toISOString().split('T')[0]
    await supabase.from('pos_gold_rates').upsert({
      seller_id: sellerId, effective_date: today,
      rate_22k:    goldForm.rate_22k    ? Number(goldForm.rate_22k)    : null,
      rate_18k:    goldForm.rate_18k    ? Number(goldForm.rate_18k)    : null,
      rate_14k:    goldForm.rate_14k    ? Number(goldForm.rate_14k)    : null,
      rate_silver: goldForm.rate_silver ? Number(goldForm.rate_silver) : null,
      rate_platinum: goldForm.rate_platinum ? Number(goldForm.rate_platinum) : null,
      buyback_22k:    goldForm.buyback_22k    ? Number(goldForm.buyback_22k)    : null,
      buyback_18k:    goldForm.buyback_18k    ? Number(goldForm.buyback_18k)    : null,
      buyback_14k:    goldForm.buyback_14k    ? Number(goldForm.buyback_14k)    : null,
      buyback_silver: goldForm.buyback_silver ? Number(goldForm.buyback_silver) : null,
      set_by: 'Settings',
    }, { onConflict: 'seller_id,effective_date' })
    await fetchGoldRates()
    setSavingGold(false)
    showToast('Gold rates saved')
  }

  const sym = seller?.seller_currency === 'BDT' ? '৳' : '$'

  return (
    <div className="pc-fade-up">

      {/* ── POS Mode ── */}
      <Card title="POS Mode" desc="Choose the mode that matches your business — this customises the POS interface for your industry">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' }}>
          {Object.values(POS_MODES).map(mode => {
            const isActive = config.pos_mode === mode.key
            return (
              <button key={mode.key} onClick={() => setConfig(c => ({ ...c, pos_mode: mode.key }))} type="button"
                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 14px', borderRadius: 'var(--radius-md)', border: '1.5px solid', borderColor: isActive ? 'var(--primary)' : 'var(--border)', background: isActive ? 'var(--primary-light)' : 'var(--bg-card)', cursor: 'pointer', textAlign: 'left', transition: 'all 0.12s' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: 'var(--radius-sm)', background: isActive ? 'var(--primary)' : 'var(--bg-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isActive ? '#fff' : 'var(--text-muted)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={mode.icon}/></svg>
                </div>
                <span style={{ fontSize: '12.5px', fontWeight: 600, color: isActive ? 'var(--primary)' : 'var(--text-primary)' }}>{mode.label}</span>
                {isActive && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 'auto', flexShrink: 0 }}><polyline points="20 6 9 17 4 12"/></svg>}
              </button>
            )
          })}
        </div>
      </Card>

      {/* ── Staff Management ── */}
      <Card title="Staff Management" desc={canManageStaff ? 'Add up to 5 staff members with individual PINs and roles' : 'Upgrade to Business plan to add staff members'}>
        {!canManageStaff ? (
          <div style={{ padding: '16px', textAlign: 'center', background: 'var(--bg-muted)', borderRadius: 'var(--radius-md)' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 10px', display: 'block' }}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>Business plan required</div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>Add staff with individual PINs and role-based access on the Business plan</div>
            <a href="/pricing" style={{ display: 'inline-block', padding: '8px 18px', borderRadius: 'var(--radius-md)', background: 'var(--primary)', color: '#fff', fontSize: '12.5px', fontWeight: 700, textDecoration: 'none' }}>Upgrade to Business →</a>
          </div>
        ) : (
          <>
            {/* Staff list */}
            {staffLoading ? (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>Loading staff...</div>
            ) : staff.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', background: 'var(--bg-muted)', borderRadius: 'var(--radius-md)', marginBottom: '12px' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 10px', display: 'block' }}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>No staff added yet</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Add staff members to track who made each sale and enable PIN login at POS</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                {staff.map(s => {
                  const roleInfo = STAFF_ROLES[s.role] || STAFF_ROLES.cashier
                  const roleColors = { owner: { bg: 'var(--primary-light)', color: 'var(--primary)' }, manager: { bg: '#FFF8E6', color: 'var(--warning)' }, cashier: { bg: 'var(--bg-muted)', color: 'var(--text-muted)' } }
                  const rc = roleColors[s.role] || roleColors.cashier
                  return (
                    <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: s.is_active ? 'var(--bg-card)' : 'var(--bg-muted)', opacity: s.is_active ? 1 : 0.6 }}>
                      {/* Avatar */}
                      <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--primary)' }}>{s.name.slice(0,1).toUpperCase()}</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{s.name}</span>
                          <span style={{ fontSize: '10.5px', fontWeight: 700, padding: '1px 8px', borderRadius: 'var(--radius-full)', background: rc.bg, color: rc.color }}>{roleInfo.label}</span>
                          {!s.is_active && <span style={{ fontSize: '10.5px', color: 'var(--text-muted)' }}>Inactive</span>}
                        </div>
                        {s.branch && <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginTop: '1px' }}>📍 {s.branch}</div>}
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px' }}>PIN: ••••</div>
                      </div>
                      <div style={{ display: 'flex', gap: '5px', flexShrink: 0 }}>
                        <button onClick={() => openEditStaff(s)}
                          style={{ padding: '5px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '11.5px', fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer' }}>
                          Edit
                        </button>
                        <button onClick={() => handleToggleStaffActive(s.id, s.is_active)}
                          style={{ padding: '5px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer' }}>
                          {s.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button onClick={() => setDeleteConfirm(s.id)}
                          style={{ padding: '5px 10px', borderRadius: 'var(--radius-sm)', border: 'none', background: '#FEF2F2', fontSize: '11.5px', fontWeight: 600, color: 'var(--error)', cursor: 'pointer' }}>
                          Remove
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Add staff button */}
            {staff.filter(s => s.is_active).length < 5 && (
              <button onClick={openAddStaff}
                style={{ width: '100%', padding: '10px', borderRadius: 'var(--radius-md)', border: '1.5px dashed var(--border)', background: 'var(--bg-muted)', fontSize: '12.5px', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add staff member
              </button>
            )}

            {/* Add/Edit staff form */}
            {showAddStaff && (
              <div style={{ marginTop: '12px', padding: '16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--primary-border)', background: 'var(--primary-light)' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px' }}>
                  {editingStaff ? `Edit — ${editingStaff.name}` : 'Add new staff member'}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                  <div style={{ gridColumn: 'span 2' }}>
                    <Label>Full name *</Label>
                    <input value={staffForm.name} onChange={e => setStaffForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Rahim Ahmed" style={inp} onFocus={inpF} onBlur={inpB} />
                  </div>
                  <div>
                    <Label>Role *</Label>
                    <select value={staffForm.role} onChange={e => setStaffForm(f => ({ ...f, role: e.target.value }))} style={inp} onFocus={inpF} onBlur={inpB}>
                      {Object.entries(STAFF_ROLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label optional>Branch</Label>
                    <input value={staffForm.branch} onChange={e => setStaffForm(f => ({ ...f, branch: e.target.value }))} placeholder="e.g. Main branch" style={inp} onFocus={inpF} onBlur={inpB} />
                  </div>
                  <div>
                    <Label>{editingStaff ? 'New PIN (leave blank to keep existing)' : '4-digit PIN *'}</Label>
                    <PinInput value={staffForm.pin} onChange={v => setStaffForm(f => ({ ...f, pin: v }))} placeholder={editingStaff ? 'Leave blank to keep' : 'e.g. 1234'} />
                  </div>
                  <div>
                    <Label>{staffForm.pin ? 'Confirm PIN *' : 'Confirm PIN'}</Label>
                    <PinInput value={staffForm.pinConfirm} onChange={v => setStaffForm(f => ({ ...f, pinConfirm: v }))} placeholder="Re-enter PIN" />
                  </div>
                </div>

                {/* Role permissions preview */}
                {staffForm.role && (
                  <div style={{ padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: 'var(--bg-card)', border: '1px solid var(--border)', marginBottom: '10px', fontSize: '11.5px', color: 'var(--text-muted)', lineHeight: 1.7 }}>
                    <strong style={{ color: 'var(--text-primary)' }}>{STAFF_ROLES[staffForm.role]?.label} permissions:</strong>
                    {' '}Can void: {STAFF_ROLES[staffForm.role]?.canVoid ? '✓' : '✗'}
                    {' '}· Override return: {STAFF_ROLES[staffForm.role]?.canOverrideReturn ? '✓' : '✗'}
                    {' '}· View reports: {STAFF_ROLES[staffForm.role]?.canViewReports ? '✓' : '✗'}
                  </div>
                )}

                {staffError && <div style={{ color: 'var(--error)', fontSize: '12.5px', marginBottom: '10px', padding: '8px 12px', background: '#FEF2F2', borderRadius: 'var(--radius-sm)' }}>{staffError}</div>}

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => { setShowAddStaff(false); setStaffError('') }}
                    style={{ flex: 1, padding: '10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '12.5px', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer' }}>
                    Cancel
                  </button>
                  <button onClick={handleSaveStaff} disabled={staffSaving}
                    style={{ flex: 2, padding: '10px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--primary)', fontSize: '12.5px', fontWeight: 700, color: '#fff', cursor: 'pointer', opacity: staffSaving ? 0.7 : 1 }}>
                    {staffSaving ? 'Saving...' : editingStaff ? 'Update staff' : 'Add staff'}
                  </button>
                </div>
              </div>
            )}

            {/* Delete confirmation */}
            {deleteConfirm && (
              <div style={{ marginTop: '10px', padding: '14px', borderRadius: 'var(--radius-md)', background: '#FEF2F2', border: '1px solid #FECACA' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--error)', marginBottom: '10px' }}>Remove this staff member? They will no longer be able to log in to POS.</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => setDeleteConfirm(null)} style={{ flex: 1, padding: '8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer' }}>Cancel</button>
                  <button onClick={() => handleDeleteStaff(deleteConfirm)} style={{ flex: 1, padding: '8px', borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--error)', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>Remove</button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* ── POS Configuration ── */}
      <Card title="POS Configuration" desc="Default settings applied to every POS session">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
          <div>
            <Label>Default branch</Label>
            <input value={config.pos_default_branch} onChange={e => setConfig(c => ({ ...c, pos_default_branch: e.target.value }))}
              placeholder="e.g. Main branch" style={inp} onFocus={inpF} onBlur={inpB} />
          </div>
          <div>
            <Label>Return window (days)</Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input type="number" value={config.pos_return_window} onChange={e => setConfig(c => ({ ...c, pos_return_window: e.target.value }))}
                placeholder="0" min="0" max="365" style={{ ...inp, width: '80px', textAlign: 'center', fontWeight: 700 }} onFocus={inpF} onBlur={inpB} />
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>days · 0 = no limit</span>
            </div>
          </div>
          <div>
            <Label>Default tax name</Label>
            <input value={config.pos_tax_name} onChange={e => setConfig(c => ({ ...c, pos_tax_name: e.target.value }))}
              placeholder="VAT" style={inp} onFocus={inpF} onBlur={inpB} />
          </div>
          <div>
            <Label>Default tax %</Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input type="number" value={config.pos_tax_percent} onChange={e => setConfig(c => ({ ...c, pos_tax_percent: e.target.value }))}
                placeholder="0" min="0" max="100" step="0.5" style={{ ...inp, width: '80px', textAlign: 'center', fontWeight: 700 }} onFocus={inpF} onBlur={inpB} />
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>%</span>
            </div>
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <Label optional>Receipt footer text</Label>
            <input value={config.pos_receipt_footer} onChange={e => setConfig(c => ({ ...c, pos_receipt_footer: e.target.value }))}
              placeholder="e.g. Thank you for shopping with us! No exchange after 7 days." style={inp} onFocus={inpF} onBlur={inpB} />
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
          <SToggle
            label="Require staff PIN at POS login"
            desc="Cashiers must select their name and enter their PIN before starting a POS session"
            value={config.pos_require_pin}
            onChange={v => setConfig(c => ({ ...c, pos_require_pin: v }))}
          />
        </div>

        <button onClick={handleSaveConfig} disabled={savingConfig}
          style={{ width: '100%', marginTop: '14px', padding: '11px', borderRadius: 'var(--radius-md)', background: 'var(--primary)', border: 'none', fontSize: '13px', fontWeight: 700, color: '#fff', cursor: 'pointer', opacity: savingConfig ? 0.7 : 1 }}>
          {savingConfig ? 'Saving...' : 'Save POS settings'}
        </button>
      </Card>

      {/* ── Gold / Metal Rates (jewellery mode only) ── */}
      {config.pos_mode === 'jewellery' && (
        <Card title="Gold & Metal Rates" desc="Set today's selling and buyback rates. These persist until you update them again.">
          <div style={{ padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: '#FFF8E6', border: '1px solid #FDE68A', fontSize: '12px', color: 'var(--warning)', marginBottom: '14px', lineHeight: 1.6 }}>
            <strong>Selling rates</strong> are used to calculate product prices at POS. <strong>Buyback rates</strong> are what you pay when a customer sells old gold to you — set your own margin here.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '8px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Metal</div>
            <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Sell ({sym}/g)</div>
            <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Buyback ({sym}/g)</div>
          </div>

          {[
            { label: '22K Gold',  sell: 'rate_22k',    buy: 'buyback_22k' },
            { label: '18K Gold',  sell: 'rate_18k',    buy: 'buyback_18k' },
            { label: '14K Gold',  sell: 'rate_14k',    buy: 'buyback_14k' },
            { label: 'Silver',    sell: 'rate_silver', buy: 'buyback_silver' },
            { label: 'Platinum',  sell: 'rate_platinum', buy: null },
          ].map(m => (
            <div key={m.label} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text-primary)' }}>{m.label}</span>
              <input type="number" value={goldForm[m.sell]||''} onChange={e => setGoldForm(f => ({ ...f, [m.sell]: e.target.value }))}
                placeholder="0" style={{ ...inp, padding: '7px 10px', fontSize: '12.5px', fontWeight: 700 }} onFocus={inpF} onBlur={inpB} />
              {m.buy
                ? <input type="number" value={goldForm[m.buy]||''} onChange={e => setGoldForm(f => ({ ...f, [m.buy]: e.target.value }))}
                    placeholder="0" style={{ ...inp, padding: '7px 10px', fontSize: '12.5px' }} onFocus={inpF} onBlur={inpB} />
                : <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', padding: '7px 10px' }}>N/A</div>
              }
            </div>
          ))}

          {/* Margin preview */}
          {goldForm.rate_22k && goldForm.buyback_22k && (
            <div style={{ marginTop: '8px', padding: '10px 12px', borderRadius: 'var(--radius-sm)', background: 'var(--bg-muted)', fontSize: '12px', color: 'var(--text-muted)' }}>
              22K margin: <strong style={{ color: 'var(--text-primary)' }}>{sym}{(Number(goldForm.rate_22k) - Number(goldForm.buyback_22k)).toLocaleString()}/g</strong>
              {' '}({Math.round((1 - Number(goldForm.buyback_22k) / Number(goldForm.rate_22k)) * 100)}% below sell rate)
            </div>
          )}

          {goldRates?.created_at && (
            <div style={{ marginTop: '8px', fontSize: '11.5px', color: 'var(--text-muted)' }}>
              Last updated: {new Date(goldRates.created_at).toLocaleString('en-BD', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              {goldRates.set_by && ` by ${goldRates.set_by}`}
            </div>
          )}

          <button onClick={handleSaveGoldRates} disabled={savingGold}
            style={{ width: '100%', marginTop: '14px', padding: '11px', borderRadius: 'var(--radius-md)', background: 'var(--warning)', border: 'none', fontSize: '13px', fontWeight: 700, color: '#fff', cursor: 'pointer', opacity: savingGold ? 0.7 : 1 }}>
            {savingGold ? 'Saving...' : 'Save gold rates'}
          </button>
        </Card>
      )}

      {/* ── Karat reference ── */}
      {config.pos_mode === 'jewellery' && (
        <Card title="Karat Reference" desc="Purity reference for gold types — informational only">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
            {KARAT_OPTIONS.map(k => (
              <div key={k.key} style={{ padding: '10px 12px', borderRadius: 'var(--radius-sm)', background: 'var(--bg-muted)', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--warning)' }}>{k.label}</div>
                <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginTop: '2px' }}>{(k.purity * 100).toFixed(1)}% pure</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}